import { useEffect, useRef, useState } from 'react';
import isEmpty from 'lodash/fp/isEmpty';
import { useGetAssetsQuery } from '../../services/assetsApi';
import { useGetTagsQuery } from '../../services/tagsApi';

import AssetTree from '@rio-cloud/rio-uikit/AssetTree';
import TreeCategory from '@rio-cloud/rio-uikit/TreeCategory';
import Tree, { type TreeGroup, type TreeItem, type SelectionChangeResponse } from '@rio-cloud/rio-uikit/Tree';
import TreeOption from '@rio-cloud/rio-uikit/TreeOption';
import { useSelectAssetsStore } from '../../services/selectAssetsStore';
import { debounce } from 'lodash';
import useScoreStore from '../../services/scoreStore';

const CATEGORY_TRUCKS = 'trucks';

const defaultExpandedTruckGroups: string[] = [];

const getFleets = (items: string[] | undefined) => {
    if (items === undefined || items.length === 0) {
        return ['sem-frota']
    } else {
        return items.map(tag => tag);
    }

}

const AssetTreeLCM = () => {
    const { data: assets, error: errorAssets, isLoading: iisLoadingAssets } = useGetAssetsQuery();
    const { data: tags, error: errorTags, isLoading: isLoadingTags } = useGetTagsQuery();
    const { setSelectedAssets } = useSelectAssetsStore();
    const { setLoading } = useScoreStore();
    const [trucks, setTrucks] = useState<TreeItem[]>([]);
    const [truckGroups, setTruckGroups] = useState<TreeGroup[]>([]);
    const [selectedTruckGroupIds, setSelectedTruckGroupIds] = useState<string[]>([]);
    const [selectedTruckIds, setSelectedTruckIds] = useState<string[]>([]);
    const [expandedTruckGroups, setExpandedTruckGroups] = useState(defaultExpandedTruckGroups);
    const [currentCategoryId, setCurrentCategoryId] = useState(CATEGORY_TRUCKS);
    const [isTreeOpen, setIsTreeOpen] = useState(true);
    const [searchValue, setSearchValue] = useState('');
    const [showEmptyGroups, setShowEmptyGroups] = useState(false);
    const [showAssetGroups, setShowAssetGroups] = useState(true);

    const debouncedSelectAssets = useRef(debounce(nextValue => setSelectedAssets(nextValue), 3000)).current

    const handleSelectTruck = async ({ items, groups }: SelectionChangeResponse) => {
        setSelectedTruckIds(items);
        setSelectedTruckGroupIds(groups);
        const localAssets = assets ?? { items: [] };
        const groupItems = localAssets?.items
            .filter(asset => asset.tags?.some(tag => groups.includes(tag)) )
            .map(asset => asset.id);

        let withoutGroup: string[] = []
        if(groups.includes('sem-frota')){
            withoutGroup = trucks.filter(truck => truck.groupIds?.includes('sem-frota')).map(asset => asset.id);
        }
        
        if (items.length == 0 && groupItems.length == 0 && withoutGroup.length == 0) {
            setSelectedAssets([]);
            debouncedSelectAssets.cancel();
            setLoading(false);
        } else{
            debouncedSelectAssets(new Set([...items, ...groupItems, ...withoutGroup]));
            setLoading(true);
        }
    };

    const handleExpandTruckGroups = (newExpandedTruckGroups: string[]) =>
        setExpandedTruckGroups(newExpandedTruckGroups);

    const handleChangeCategories = (categoryId: string) => setCurrentCategoryId(categoryId);
    const handleUpdateSearch = (value: string) => setSearchValue(value);
    const handleToggleTree = (toggle: boolean) => setIsTreeOpen(toggle);
    const handleToggleEmptyGroups = () => setShowEmptyGroups(!showEmptyGroups);
    const handleToggleAssetGroups = () => setShowAssetGroups(!showAssetGroups);

    useEffect(() => {
        if (assets?.items) {
            setTrucks(
                assets.items.map(asset => ({
                    id: asset.id,
                    name: asset.name,
                    type: asset.type,
                    groupIds: getFleets(asset.tags)
                }))
            );
        }
    }, [assets]);

    useEffect(() => {
        if (tags?.items) {
            const tags_list = tags.items.map(tag => ({
                id: tag.id,
                name: tag.name,
            }));
            const no_tag = {
                id: "sem-frota",
                name: "Sem frota"
            }
            setTruckGroups([...tags_list, no_tag]);
        }
    }, [tags]);

    return (
        <AssetTree
            minWidth={300}
            maxWidth={450}
            currentCategoryId={currentCategoryId}
            onCategoryChange={handleChangeCategories}
            isOpen={isTreeOpen}
            onToggleTree={handleToggleTree}
        >
            <TreeCategory
                key={CATEGORY_TRUCKS}
                id={CATEGORY_TRUCKS}
                label='Assets'
                icon='rioglyph-truck'
                hasSelection={!isEmpty(selectedTruckIds) || !isEmpty(selectedTruckGroupIds)}
            >
                <Tree
                    groups={showAssetGroups ? truckGroups : []}
                    items={trucks}
                    expandedGroups={expandedTruckGroups}
                    onExpandGroupsChange={handleExpandTruckGroups}
                    selectedGroups={selectedTruckGroupIds}
                    selectedItems={selectedTruckIds}
                    onSelectionChange={handleSelectTruck}
                    searchPlaceholder='Buscar veículo'
                    onSearchChange={handleUpdateSearch}
                    showEmptyGroups={showEmptyGroups}
                    treeOptionsTooltip='Vehicle tree options'
                    treeOptions={[
                        <TreeOption
                            key='emptyGroupsToggle'
                            label='Show empty groups'
                            isChecked={showEmptyGroups}
                            onChange={handleToggleEmptyGroups}
                        />,
                        <TreeOption
                            key='assetGroupsToggle'
                            label='Show groups'
                            isChecked={showAssetGroups}
                            onChange={handleToggleAssetGroups}
                        />,
                    ]}
                />
            </TreeCategory>
        </AssetTree>
    );
};

export default AssetTreeLCM;
